import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Divider,
  FormControlLabel,
  Checkbox,
  FormGroup,
} from "@mui/material";
import DialogContainer from "../../../../../../components/dialog-container/dialog-container";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import * as yup from "yup";
import {
  getServicesRequest,
  getInspectionServicesRequest,
  getCurrentServicesRequest,
} from "../../../../../../redux/inspection-firms/slice";
import Loading from "../../../../../../components/loading/loading";
import ErrorNotification from "../../../../../../components/error-notification/error-notification";
import {
  updateCurrentServicesDetailsRequest,
  updateServicesDetailsRequest,
} from "../../../../../../utils/APICalls";

type props = {
  showDialog: boolean;
  closeDialog: any;
  data?: any;
};

interface FormValues {
  agencyInspections: string[];
  bankingInspections: string[];
}

export default function EditServices({ showDialog, closeDialog, data }: props) {
  const dispatch = useDispatch();
  const [showLoading, setShowLoading] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const { inspectionServices, loading, error } = useSelector(
    (state: any) => state.inspectionFirms
  );

  useEffect(() => {
    dispatch(getInspectionServicesRequest());
  }, [dispatch]);

  const ValidationSchema = yup.object({
    agencyInspections: yup
      .array()
      .of(yup.string())
      .test(
        "at-least-one-agency-or-banking",
        "This field can't be empty",
        function (value) {
          const agency = value || [];
          const banking = (this.parent?.bankingInspections as string[]) || [];
          return agency.length > 0 || banking.length > 0;
        }
      ),
    bankingInspections: yup
      .array()
      .of(yup.string())
      .test(
        "at-least-one-agency-or-banking",
        "This field can't be empty",
        function (value) {
          const banking = value || [];
          const agency = (this.parent?.agencyInspections as string[]) || [];
          return banking.length > 0 || agency.length > 0;
        }
      ),
  });

  const formik = useFormik<FormValues>({
    initialValues: {
      agencyInspections:
        data?.agencyInspections?.map((type: any) => type.inspectionType) || [],
      bankingInspections:
        data?.bankingInspections?.map((type: any) => type.inspectionType) || [],
    },
    validationSchema: ValidationSchema,
    onSubmit: async (values) => {
      const isValid = await formik.validateForm();
      if (!isValid) {
        formik.setTouched({
          agencyInspections: true,
          bankingInspections: true,
        });
        return;
      }
      if (data?.id) {
        updateServicesDetails(values);
      } else {
        updateCurrentServicesDetails(values);
      }
    },
    enableReinitialize: true,
  });

  const updateServicesDetails = async (values: any) => {
    const payload = {
      id: data?.id,
      inspectionTypes: [
        ...values.agencyInspections,
        ...values.bankingInspections,
      ],
    };
    setShowLoading(true);
    const response = await updateServicesDetailsRequest(payload);
    setShowLoading(false);
    if (response?.status === 204) {
      dispatch(getServicesRequest({ id: data?.id }));
      hideDialog();
    } else if (response?.status === 400) {
      setShowErrorNotification(true);
      setErrorMessage(response?.data?.detail);
    }
  };

  const updateCurrentServicesDetails = async (values: any) => {
    const payload = {
      inspectionTypes: [
        ...values.agencyInspections,
        ...values.bankingInspections,
      ],
    };
    setShowLoading(true);
    const response = await updateCurrentServicesDetailsRequest(payload);
    setShowLoading(false);
    if (response?.status === 204) {
      dispatch(getCurrentServicesRequest());
      hideDialog();
    } else if (response?.status === 400 || response?.status === 404) {
      setShowErrorNotification(true);
      setErrorMessage(response?.data?.detail);
    }
  };

  const hideDialog = () => {
    formik.resetForm();
    closeDialog();
  };

  return (
    <DialogContainer
      title="Inspection Firm - Services Offered"
      showDialog={showDialog}
      closeDialog={closeDialog}
      onSubmit={formik.handleSubmit}
    >
      <form onSubmit={formik.handleSubmit}>
        <Box className="user-name-layout">
          <Typography className="label-txt">Agency Inspections</Typography>
          <FormGroup>
            {inspectionServices
              .filter((type: any) => type.category === "Agency")
              .map((type: any) => (
                <FormControlLabel
                  key={type.inspectionType}
                  control={
                    <Checkbox
                      checked={formik.values.agencyInspections?.includes(
                        type.inspectionType
                      )}
                      onBlur={() =>
                        formik.setFieldTouched("agencyInspections", true)
                      }
                      onChange={() => {
                        const set = new Set(formik.values.agencyInspections);
                        if (set.has(type.inspectionType)) {
                          set.delete(type.inspectionType);
                        } else {
                          set.add(type.inspectionType);
                        }
                        formik.setFieldValue(
                          "agencyInspections",
                          Array.from(set)
                        );
                      }}
                      name={type.inspectionType}
                    />
                  }
                  label={type.displayName}
                />
              ))}
            {formik.touched.agencyInspections &&
              formik.errors.agencyInspections && (
                <Typography color="error" variant="caption">
                  {formik.errors.agencyInspections}
                </Typography>
              )}
          </FormGroup>
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="label-txt">
            Commercial Banking
            <br />
            Inspections
          </Typography>
          <FormGroup>
            {inspectionServices
              .filter((type: any) => type.category === "Commercial")
              .map((type: any) => (
                <FormControlLabel
                  key={type.inspectionType}
                  control={
                    <Checkbox
                      checked={formik.values.bankingInspections?.includes(
                        type.inspectionType
                      )}
                      onBlur={() =>
                        formik.setFieldTouched("bankingInspections", true)
                      }
                      onChange={() => {
                        const set = new Set(formik.values.bankingInspections);
                        if (set.has(type.inspectionType)) {
                          set.delete(type.inspectionType);
                        } else {
                          set.add(type.inspectionType);
                        }
                        formik.setFieldValue(
                          "bankingInspections",
                          Array.from(set)
                        );
                      }}
                      name={type.inspectionType}
                    />
                  }
                  label={type.displayName}
                />
              ))}
            {formik.touched.bankingInspections &&
              formik.errors.bankingInspections && (
                <Typography color="error" variant="caption">
                  {formik.errors.bankingInspections}
                </Typography>
              )}
          </FormGroup>
        </Box>
        <Typography
          sx={{
            color: "rgba(0, 0, 0, 0.6) !important",
            mt: 2,
            fontStyle: "italic",
            fontSize: "14px",
          }}
        >
          *In order to receive these types of inspections, at least 1 certified
          inspector must be active in your firm.
        </Typography>
      </form>
      {(loading || showLoading) && <Loading />}
      {(error || showErrorNotification) && (
        <ErrorNotification
          show={showErrorNotification}
          message={error || errorMessage}
          onClose={() => setShowErrorNotification(false)}
        />
      )}
    </DialogContainer>
  );
}
